import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';

export default function TrialEntryPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white text-slate-900 relative overflow-hidden">
      <PageTitle title={t('entry.pageTitle')} />

      {/* Background gradient decoration */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[560px]"
        style={{
          background:
            'radial-gradient(ellipse 90% 55% at 50% -5%, #ede9fe 0%, #e0e7ff 30%, transparent 75%)',
        }}
      />

      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 max-w-6xl mx-auto">
        <a href="https://clareefai.com">
          <img src="/logo-color.png" alt="Clareefai" className="h-8 w-auto" />
        </a>
        <Link
          to="/login"
          className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          {t('entry.signIn')}
        </Link>
      </header>

      {/* Hero */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-20 pb-32 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-xs font-medium text-violet-700 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          14-day free trial · No credit card required
        </div>

        <h1 className="text-5xl sm:text-6xl font-bold leading-tight tracking-tight text-slate-900 mb-6">
          {t('entry.headline')}{' '}
          <span className="bg-gradient-to-r from-violet-600 to-indigo-500 bg-clip-text text-transparent">
            {t('entry.headlineAccent')}
          </span>
        </h1>

        <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('entry.subheadline')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/trial/signup"
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 rounded-xl bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white font-semibold text-base transition-colors shadow-lg shadow-violet-200"
          >
            {t('entry.startTrial')}
          </Link>
          <Link
            to="/login"
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 rounded-xl border border-gray-200 bg-white text-slate-700 hover:border-gray-300 hover:bg-gray-50 font-medium text-base transition-colors"
          >
            {t('entry.signIn')}
          </Link>
        </div>

        <p className="mt-12 text-xs text-slate-400 tracking-widest uppercase">
          {t('entry.trustedBy')}
        </p>

        {/* Feature grid */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-5 text-left">
          {[
            { title: t('entry.feature1Title'), desc: t('entry.feature1Desc'), icon: '🤝' },
            { title: t('entry.feature2Title'), desc: t('entry.feature2Desc'), icon: '⭐' },
            { title: t('entry.feature3Title'), desc: t('entry.feature3Desc'), icon: '🔗' },
          ].map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="text-2xl mb-3">{f.icon}</div>
              <h3 className="font-semibold text-slate-900 mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </main>

      <footer className="relative z-10 border-t border-gray-100 py-6 px-6 text-center">
        <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
          <a
            href="https://clareefai.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-slate-600 transition-colors"
          >
            Privacy Policy
          </a>
          <span>·</span>
          <a
            href="https://clareefai.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-slate-600 transition-colors"
          >
            Terms of Service
          </a>
        </div>
      </footer>
    </div>
  );
}
